import React, { useState, useCallback } from 'react';
import { Search, Plane, Clock, Filter, Calendar, MapPin, Loader, AlertCircle, CheckCircle } from 'lucide-react';

function FlightSearchApp() {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [maxStops, setMaxStops] = useState(2);
  const [minConnectionTime, setMinConnectionTime] = useState(45);
  const [maxConnectionTime, setMaxConnectionTime] = useState(240);
  const [departureRange] = useState({ min: '00:00', max: '23:59' });
  const [arrivalRange] = useState({ min: '00:00', max: '23:59' });
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [searchSummary, setSearchSummary] = useState(null);

  // Airline colors for badges
  const airlineColors = {
    'United': 'bg-blue-100 text-blue-800',
    'United Airlines': 'bg-blue-100 text-blue-800',
    'Southwest': 'bg-orange-100 text-orange-800',
    'Southwest Airlines': 'bg-orange-100 text-orange-800',
    'American': 'bg-red-100 text-red-800',
    'American Airlines': 'bg-red-100 text-red-800',
    'Delta': 'bg-purple-100 text-purple-800',
    'Delta Air Lines': 'bg-purple-100 text-purple-800',
    'Frontier': 'bg-green-100 text-green-800',
    'Frontier Airlines': 'bg-green-100 text-green-800',
    'Alaska': 'bg-teal-100 text-teal-800',
    'Alaska Airlines': 'bg-teal-100 text-teal-800',
    'JetBlue': 'bg-indigo-100 text-indigo-800',
    'JetBlue Airways': 'bg-indigo-100 text-indigo-800',
    'Spirit': 'bg-yellow-100 text-yellow-800',
    'Spirit Airlines': 'bg-yellow-100 text-yellow-800',
    'Allegiant': 'bg-pink-100 text-pink-800',
    'Hawaiian': 'bg-purple-100 text-purple-800',
    'default': 'bg-gray-100 text-gray-800'
  };

  const fetchFlightData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRoutes([]);
    setSearchSummary(null);

    const params = new URLSearchParams({
      origin: origin.toUpperCase(),
      destination: destination.toUpperCase(),
      date,
      maxStops: maxStops.toString(),
      minConnectionTime: minConnectionTime.toString(),
      maxConnectionTime: maxConnectionTime.toString(),
      departureMin: departureRange.min,
      departureMax: departureRange.max,
      arrivalMin: arrivalRange.min,
      arrivalMax: arrivalRange.max
    });

    try {
      const response = await fetch(`/api/search-flights?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch flight data');
      }

      const data = await response.json();
      setRoutes(data.routes || []);
      setSearchSummary(data.summary || null);
      
      if (data.routes.length === 0) {
        setError('No flights found for the selected criteria. Try adjusting your filters.');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching flight data:', err);
    } finally {
      setLoading(false);
    }
  }, [origin, destination, date, maxStops, minConnectionTime, maxConnectionTime, departureRange, arrivalRange]);

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getAirlineColor = (airline) => {
    return airlineColors[airline] || airlineColors.default;
  };

  const formatTime = (time) => {
    // Handle both 24h and 12h formats
    if (!time) return 'N/A';
    return time;
  };

  return (
    <>
      <style>{`
        /* FlightSearch dark mode — .dark class (Tailwind class-based) */

        /* Page background */
        .dark .fs-root { background: #0f172a !important; }

        /* White cards */
        .dark .fs-root .bg-white {
          background-color: rgb(20 27 46) !important;
          border-color: rgb(51 65 85) !important;
        }

        /* Gray backgrounds */
        .dark .fs-root .bg-gray-50   { background-color: rgb(30 41 59) !important; }
        .dark .fs-root .bg-gray-100  { background-color: rgb(30 41 59) !important; }

        /* Blue tint areas */
        .dark .fs-root .bg-blue-50   { background-color: rgb(15 30 70) !important; }
        .dark .fs-root .bg-red-50    { background-color: rgb(50 10 10) !important; }

        /* Borders */
        .dark .fs-root .border-gray-200 { border-color: rgb(51 65 85) !important; }
        .dark .fs-root .border-red-200  { border-color: rgb(153 27 27) !important; }

        /* Text */
        .dark .fs-root .text-gray-800 { color: rgb(241 245 249) !important; }
        .dark .fs-root .text-gray-700 { color: rgb(226 232 240) !important; }
        .dark .fs-root .text-gray-600 { color: rgb(203 213 225) !important; }
        .dark .fs-root .text-gray-500 { color: rgb(148 163 184) !important; }
        .dark .fs-root .text-gray-400 { color: rgb(100 116 139) !important; }
        .dark .fs-root .text-gray-300 { color: rgb(71 85 105)  !important; }
        .dark .fs-root .text-blue-700 { color: rgb(147 197 253) !important; }
        .dark .fs-root .text-blue-600 { color: rgb(147 197 253) !important; }
        .dark .fs-root .text-purple-600 { color: rgb(196 181 253) !important; }
        .dark .fs-root .text-red-700  { color: rgb(252 165 165) !important; }
        .dark .fs-root .text-green-600 { color: rgb(74 222 128) !important; }

        /* Labels */
        .dark .fs-root label { color: rgb(226 232 240) !important; }

        /* Inputs */
        .dark .fs-root input[type="text"],
        .dark .fs-root input[type="date"],
        .dark .fs-root input[type="search"],
        .dark .fs-root select {
          background-color: rgb(15 23 42) !important;
          border-color: rgb(71 85 105) !important;
          color: rgb(248 250 252) !important;
        }
        .dark .fs-root input::placeholder { color: rgb(100 116 139) !important; }
        .dark .fs-root input:focus,
        .dark .fs-root select:focus {
          border-color: rgb(59 130 246) !important;
          box-shadow: 0 0 0 2px rgba(59,130,246,0.3) !important;
          background-color: rgb(15 23 42) !important;
        }

        /* Flight result segments */
        .dark .fs-root .bg-gray-50.rounded-lg { background-color: rgb(30 41 59) !important; }
        .dark .fs-root .bg-blue-50.rounded    { background-color: rgb(15 30 70) !important; }
        .dark .fs-root .text-blue-700.rounded  { color: rgb(147 197 253) !important; }

        /* Divider lines */
        .dark .fs-root .bg-gray-300 { background-color: rgb(51 65 85) !important; }
        .dark .fs-root .border-t { border-color: rgb(51 65 85) !important; }

        /* Status badges */
        .dark .fs-root .bg-gray-100.text-gray-800 {
          background-color: rgb(51 65 85) !important;
          color: rgb(226 232 240) !important;
        }
        .dark .fs-root .bg-purple-100 {
          background-color: rgb(59 7 100) !important;
        }
        .dark .fs-root .text-purple-700 { color: rgb(196 181 253) !important; }

        /* Airline logo bg */
        .dark .fs-root .bg-white.p-0\\.5 { background-color: rgb(51 65 85) !important; }

        /* Hover states */
        .dark .fs-root .hover\\:shadow-lg:hover {
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.5) !important;
        }

        /* Scrollbar */
        .dark .fs-root ::-webkit-scrollbar-track { background: rgb(30 41 59); }
        .dark .fs-root ::-webkit-scrollbar-thumb { background: rgb(71 85 105); border-radius: 4px; }
      `}</style>
    <div className="fs-root min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:bg-none dark:bg-slate-900 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-xl p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
            <Plane className="text-blue-600" />
            Non-Rev Flight Schedule Search
          </h1>
          {/* <p className="text-sm text-gray-600 mb-6">
            Multi-airline routing powered by Google Flights (via SerpApi)
          </p> */}
          
          {/* Search Form */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value.toUpperCase())}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  placeholder="Airport Code"
                  maxLength="3"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value.toUpperCase())}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  placeholder="Airport Code"
                  maxLength="3"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
              <button
                onClick={fetchFlightData}
                disabled={loading || !origin || !destination || !date}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-400"
              >
                {loading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Searching Flights...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Search Flights
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-blue-600 hover:text-blue-700 flex items-center gap-2 text-sm font-medium"
          >
            <Filter className="h-4 w-4" />
            {showFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
          </button>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <h3 className="font-semibold text-gray-700 mb-3">Advanced Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Stops: {maxStops === 0 ? 'Direct Only' : maxStops}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="3"
                    value={maxStops}
                    onChange={(e) => setMaxStops(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Direct</span>
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Connection: {minConnectionTime} min
                  </label>
                  <input
                    type="range"
                    min="30"
                    max="120"
                    step="15"
                    value={minConnectionTime}
                    onChange={(e) => setMinConnectionTime(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Connection: {formatDuration(maxConnectionTime)}
                  </label>
                  <input
                    type="range"
                    min="60"
                    max="480"
                    step="30"
                    value={maxConnectionTime}
                    onChange={(e) => setMaxConnectionTime(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Search Summary */}
          {searchSummary && !loading && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="text-blue-700">
                  <strong>{searchSummary.totalRoutes}</strong> routes found
                </span>
                {searchSummary.directFlights > 0 && (
                  <span className="text-blue-600">
                    {searchSummary.directFlights} direct
                  </span>
                )}
                {searchSummary.oneStopFlights > 0 && (
                  <span className="text-blue-600">
                    {searchSummary.oneStopFlights} one-stop
                  </span>
                )}
                {searchSummary.interlineItineraries > 0 && (
                  <span className="text-purple-600">
                    {searchSummary.interlineItineraries} interline
                  </span>
                )}
                {searchSummary.airlines && searchSummary.airlines.length > 0 && (
                  <span className="text-gray-600">
                    Airlines: {searchSummary.airlines.join(', ')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="bg-white rounded-xl shadow-xl p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </div>
          )}
          
          {!loading && routes.length === 0 && !error && (
            <div className="text-center py-12 text-gray-500">
              <Plane className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Enter your route and click "Search Flights" to find available schedules.</p>
              <p className="text-sm mt-2">Data provided by Google Flights via SerpApi</p>
            </div>
          )}
          
          {!loading && routes.length > 0 && (
            <>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Available Routes
              </h2>
              
              <div className="space-y-4">
                {routes.map((route, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                    {/* Route Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="text-lg font-semibold text-gray-800">
                          {formatTime(route.depart)} → {formatTime(route.arrive)}
                        </div>
                        <div className="text-sm text-gray-500">
                          ({formatDuration(route.totalDuration)})
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-700">
                          {route.stops === 0 ? 'Non-stop' : `${route.stops} stop${route.stops > 1 ? 's' : ''}`}
                        </div>
                        {route.isInterline && (
                          <div className="text-xs text-purple-600 font-medium mt-1">
                            Interline
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Flight Segments */}
                    <div className="space-y-2">
                      {route.segments.map((segment, segIdx) => (
                        <React.Fragment key={`${idx}-${segIdx}`}>
                          <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="flex items-center gap-2">
                                  {segment.airlineLogo && (
                                    <img 
                                      src={segment.airlineLogo} 
                                      alt={`${segment.airline} logo`}
                                      className="w-6 h-6 rounded-sm object-contain bg-white p-0.5 shadow-sm"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  )}
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${getAirlineColor(segment.airline)}`}>
                                    {segment.airline}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {segment.flight !== 'N/A' ? segment.flight : ''}
                                </span>
                                {segment.aircraft !== 'N/A' && (
                                  <span className="text-xs text-gray-400">• {segment.aircraft}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-center">
                                  <div className="font-semibold text-lg">{formatTime(segment.depart)}</div>
                                  <div className="text-sm text-gray-600 font-medium">{segment.from}</div>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                  <div className="h-px bg-gray-300 flex-1"></div>
                                  <Plane className="h-4 w-4 text-gray-400 mx-2 rotate-90" />
                                  <div className="h-px bg-gray-300 flex-1"></div>
                                </div>
                                <div className="text-center">
                                  <div className="font-semibold text-lg">{formatTime(segment.arrive)}</div>
                                  <div className="text-sm text-gray-600 font-medium">{segment.to}</div>
                                </div>
                                {segment.duration > 0 && (
                                  <div className="ml-4 text-sm text-gray-500">
                                    {formatDuration(segment.duration)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Layover Information */}
                          {segIdx < route.segments.length - 1 && route.layovers[segIdx] && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded text-sm text-blue-700">
                              <Clock className="h-4 w-4" />
                              <span>
                                <strong>{formatDuration(route.layovers[segIdx].duration)}</strong> layover in <strong>{route.layovers[segIdx].airport}</strong>
                              </span>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    
                    {/* Route Summary */}
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                      <span>
                        Route: {route.route}
                      </span>
                      {route.airlines.length > 1 && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          {route.airlines.join(' + ')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default FlightSearchApp;